/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { globalErrorType } from "@/context/ErrorContext";

/**
 * Gets invalid properties from error object.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {object | null} Invalid properties object or null
 */
function getInvalidProperties(error: globalErrorType): object | null {
  if (!error) return null;
  const invalidProperties = error.body?.invalid_properties || error.invalid_properties;
  if (!invalidProperties || typeof invalidProperties !== 'object') {
    return null;
  }
  return invalidProperties;
}

/**
 * Extracts invalid properties from error.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {string | null} Formatted invalid properties or null
 */
function extractInvalidProperties(error: globalErrorType): string | null {
  const invalidProperties = getInvalidProperties(error);
  if (!invalidProperties) {
    return null;
  }

  const fieldNames = Object.keys(invalidProperties);
  if (fieldNames.length === 0) {
    return null;
  }

  return `Invalid Properties: ${JSON.stringify(invalidProperties, null, 2)}`;
}

/**
 * Extracts detail from error.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {string | null} Detail or null
 */
function extractDetail(error: globalErrorType): string | null {
  if (!error) return null;
  const detail = error.body?.detail || error.detail;
  return detail ? `Detail: ${detail}` : null;
}

/**
 * Extracts status information from error.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {string[]} Array of status strings
 */
function extractStatusInfo(error: globalErrorType): string[] {
  if (!error) return [];
  const statusInfo: string[] = [];
  
  if (error.status) {
    statusInfo.push(`Status: ${error.status}`);
  }
  
  if (error.statusText) {
    statusInfo.push(`Status Text: ${error.statusText}`);
  }
  
  return statusInfo;
}

/**
 * Extracts message from error.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {string | null} Message or null
 */
function extractMessage(error: globalErrorType): string | null {
  if (!error) return null;
  return error.message ? `Message: ${error.message}` : null;
}

/**
 * Adds detail to array if not null.
 * 
 * @param {string[]} array - Array to add to
 * @param {string | null} item - Item to add
 * @returns {void}
 */
function addIfNotNull(array: string[], item: string | null): void {
  if (item) {
    array.push(item);
  }
}

/**
 * Gets fallback error string.
 * 
 * @param {globalErrorType} error - Error object
 * @returns {string} Fallback error string
 */
function getFallbackError(error: globalErrorType): string {
  if (!error) return 'Error: Unknown error';
  return `Error: ${JSON.stringify(error, null, 2)}`;
}

/**
 * Extracts technical details from error object.
 * 
 * @param {globalErrorType} error - Error object from API
 * @returns {string | null} Formatted technical details or null
 */
export function extractTechnicalDetails(error: globalErrorType): string | null {
  if (!error) {
    return null;
  }

  const techDetails: string[] = [];
  
  addIfNotNull(techDetails, extractInvalidProperties(error));
  addIfNotNull(techDetails, extractDetail(error));
  techDetails.push(...extractStatusInfo(error));
  addIfNotNull(techDetails, extractMessage(error));

  if (techDetails.length === 0) {
    techDetails.push(getFallbackError(error));
  }

  return techDetails.join('\n\n');
}
